// Full-screen growth-chart inspector. Reuses GrowthChart with extended
// x-axis, taller canvas, and interactive=true so tapping anywhere on the
// chart shows the expected (P50) and projected (current-SDS) values at
// that age. Lets parents peek into the future without leaving the page.

import { useEffect } from 'react';
import type { Measurement } from '@shared/types.ts';
import type { MeasurementKind } from '@shared/constants.ts';
import { growth } from '@shared/index.ts';
import { Icon } from '../Icon.tsx';
import { GrowthChart } from './GrowthChart.tsx';
import { KIND_META } from '../../lib/measure.ts';

type Props = {
  open: boolean;
  onClose: () => void;
  kind: MeasurementKind;
  birthDate: string;
  measurements: Measurement[];
  showCurves: boolean;
  reference: growth.ReferenceId;
  sex: 'M' | 'F' | null;
  /** Latest SDS — drives the projection line. */
  currentSds: number | null;
};

// Default forward window per metric. Weight + length are most interesting
// in the first 18-24 months; HC slows down sooner.
const FORWARD_DAYS: Record<MeasurementKind, number> = {
  weight: 730, // ~24 mo
  length: 730,
  head_circumference: 365,
  temperature: 60,
};

export function GrowthChartFullSheet({
  open,
  onClose,
  kind,
  birthDate,
  measurements,
  showCurves,
  reference,
  sex,
  currentSds,
}: Props) {
  // Lock body scroll while open — mirrors SheetShell behaviour without
  // pulling in the chrome (we want a full-bleed sheet, not the standard
  // pill-shape modal).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;
  const meta = KIND_META[kind];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${meta.label} chart`}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--bg-app)',
        zIndex: 200,
        display: 'flex',
        flexDirection: 'column',
        animation: 'lt-fade-in .15s ease',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 16px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-app)',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close chart"
          style={{
            width: 36,
            height: 36,
            borderRadius: 999,
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            color: 'var(--text-2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon.chevD s={18} style={{ transform: 'rotate(90deg)' }} />
        </button>
        <div style={{ flex: 1 }}>
          <div className="lt-serif" style={{ fontSize: 18, fontWeight: 500, lineHeight: 1.1 }}>
            {meta.label} · full view
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
            Tap or drag to inspect any age
          </div>
        </div>
      </header>

      <div className="lt-scroll" style={{ flex: 1, padding: '4px 14px 16px' }}>
        <GrowthChart
          kind={kind}
          birthDate={birthDate}
          measurements={measurements}
          showCurves={showCurves}
          reference={reference}
          sex={sex}
          xMaxDays={FORWARD_DAYS[kind] ?? 540}
          // Cap below 60% so the slider + 3-row inspector still fit in
          // viewport without scrolling. Floor 320 for short screens.
          height={Math.max(320, Math.round(window.innerHeight * 0.58))}
          interactive
          currentSds={currentSds}
        />

        <p
          style={{
            margin: '12px 4px 0',
            fontSize: 11,
            color: 'var(--text-3)',
            lineHeight: 1.4,
          }}
        >
          Dashed{' '}
          <span style={{ color: `var(--${meta.accent})`, fontWeight: 600 }}>
            projection
          </span>{' '}
          extends the trend at the baby’s current SDS — informational, not
          a forecast. Babies routinely shift 1 SD up or down in year one as
          they find their channel.
        </p>
      </div>
    </div>
  );
}
